import { useRef, useCallback, useEffect } from 'react';

/**
 * Open-only hover-intent debounce. Confirms intent to open after `openDelay`
 * of sustained hover/focus and calls `onOpen`. Leaving/blurring before the
 * delay elapses cancels the pending open; leaving after it has already fired
 * is a no-op. This hook has no concept of "close" — dismissal is owned
 * elsewhere (see useMegaMenuDismiss).
 *
 * @param {object} options
 * @param {() => void} options.onOpen - Called when hover intent is confirmed.
 * @param {number} [options.openDelay=150] - Milliseconds before confirming hover intent.
 * @returns {{
 *   onMouseEnter: () => void,
 *   onMouseLeave: () => void,
 *   onFocus: () => void,
 *   onBlur: () => void,
 *   cancel: () => void
 * }}
 */
const useHoverOpenIntent = ({ onOpen, openDelay = 150 }) => {
	const openTimerRef = useRef(null);
	const onOpenRef = useRef(onOpen);

	onOpenRef.current = onOpen;

	const cancel = useCallback(() => {
		clearTimeout(openTimerRef.current);
		openTimerRef.current = null;
	}, []);

	const handleEnter = useCallback(() => {
		clearTimeout(openTimerRef.current);
		openTimerRef.current = setTimeout(() => {
			onOpenRef.current();
		}, openDelay);
	}, [openDelay]);

	const handleLeave = useCallback(() => {
		clearTimeout(openTimerRef.current);
		openTimerRef.current = null;
	}, []);

	useEffect(() => {
		return () => {
			clearTimeout(openTimerRef.current);
		};
	}, []);

	return {
		onMouseEnter: handleEnter,
		onMouseLeave: handleLeave,
		onFocus: handleEnter,
		onBlur: handleLeave,
		cancel
	};
};

export default useHoverOpenIntent;
